<template>
    <b-modal
        :visible="visible"
        :title="$t('Component(ReviewProcess):reviewable-modal-label')"
        :size="size"
        :ok-disabled="true"
        scrollable
        no-close-on-backdrop
        @hide="onHide"
    >
        <b-overlay
            variant="white"
            :show="loading"
            spinner-variant="primary"
            blur="0"
            opacity=".85"
            rounded="sm"
        >
            <b-alert
                v-if="error"
                variant="danger"
                show
            >
                {{ error }}
            </b-alert>

            <ReviewProcessFlow
                v-if="isDocumentComplianceProcess"
                ref="processFlow"
                :process="process"
                :review-capabilities="reviewCapabilities"
                @item-saved="onItemSaved"
                @state-change="onFlowStateChange"
                @cancel="$emit('close')"
            />

            <ReviewableRenderer
                v-else-if="process"
                :process="process"
                :review-capabilities="reviewCapabilities"
                @item-saved="onItemSaved"
            />
        </b-overlay>

        <template #modal-footer>
            <b-button
                variant="outline-secondary"
                :disabled="completeSubmitting"
                @click="$emit('close')"
            >
                {{ $t('Component(Button):cancel') }}
            </b-button>
            <b-button
                v-if="showSaveAndNextAction"
                variant="primary"
                :disabled="savingActiveItem"
                @click="saveActiveItem"
            >
                <b-spinner
                    v-if="savingActiveItem"
                    small
                />
                <span v-else>{{ $t('Component(Button):save-and-next') }}</span>
            </b-button>
            <b-button
                v-if="showProcessDecisionActions"
                variant="danger"
                :disabled="completeSubmitting || !allItemsReviewed"
                @click="completeProcess('reject')"
            >
                {{ $t('Component(Button):reject') }}
            </b-button>
            <b-button
                v-if="showProcessDecisionActions"
                variant="primary"
                :disabled="completeSubmitting || !allItemsReviewed"
                @click="completeProcess('continue')"
            >
                <b-spinner
                    v-if="completeSubmitting"
                    small
                />
                <span v-else>{{ $t('Component(Button):complete') }}</span>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import {
    BAlert,
    BButton,
    BModal,
    BOverlay,
    BSpinner,
} from 'bootstrap-vue'
import { ResearchReviewApi } from '@core/api'
import ReviewableRenderer from './ReviewableRenderer.vue'
import ReviewProcessFlow from './reviewable/document-compliance/ReviewProcessFlow.vue'

export default {
    name: 'ReviewProcessModal',
    components: {
        BAlert,
        BButton,
        BModal,
        BOverlay,
        BSpinner,
        ReviewProcessFlow,
        ReviewableRenderer,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        processId: {
            type: [String, Number],
            default: null,
        },
        size: {
            type: String,
            default: 'xl',
        },
        reviewCapabilities: {
            type: Object,
            default: () => ({
                canEditExtractedProducts: true,
                canEditExtractedSubstances: true,
                canEditProductLinks: true,
                canEditSubstanceLinks: true,
            }),
        },
    },
    data() {
        return {
            process: null,
            loading: false,
            error: '',
            completeSubmitting: false,
            savingActiveItem: false,
            processFlowHasActiveItem: false,
        }
    },
    computed: {
        allItemsReviewed() {
            const items = this.process && Array.isArray(this.process.items) ? this.process.items : []
            return items.length > 0 && items.every(item => item && item.status === 'reviewed')
        },
        showCompleteActions() {
            return this.process && this.process.status !== 'completed'
        },
        isDocumentComplianceProcess() {
            return this.process && this.process.reviewableType === 'document_compliance'
        },
        hasActiveProcessFlowItem() {
            return this.isDocumentComplianceProcess && this.processFlowHasActiveItem
        },
        showSaveAndNextAction() {
            return this.showCompleteActions && this.hasActiveProcessFlowItem
        },
        showProcessDecisionActions() {
            return this.showCompleteActions && (!this.isDocumentComplianceProcess || !this.hasActiveProcessFlowItem)
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.loadProcess()
                }
            },
        },
        processId() {
            if (this.visible) {
                this.loadProcess()
            }
        },
    },
    methods: {
        loadProcess() {
            if (!this.processId) {
                this.process = null
                return
            }

            this.loading = true
            this.error = ''

            ResearchReviewApi
                .getProcess(this.processId)
                .then(response => {
                    this.process = response.data
                    this.$emit('process-loaded', this.process)
                })
                .catch(error => {
                    this.error = this.getErrorMessage(error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        onFlowStateChange(state) {
            this.processFlowHasActiveItem = !!(state && state.hasActiveItem)
        },
        onItemSaved(savedItem) {
            if (!this.process || !savedItem || !savedItem.id) {
                return
            }

            const items = Array.isArray(this.process.items) ? this.process.items : []
            const index = items.findIndex(item => item && item.id === savedItem.id)
            if (index === -1) {
                return
            }

            this.$set(this.process.items, index, savedItem)
            this.process.itemsReviewed = this.process.items.filter(item => item && item.status === 'reviewed').length
            this.$emit('item-saved', savedItem)
        },
        completeProcess(decision) {
            if (!this.process || !this.process.processId || this.completeSubmitting) {
                return
            }

            this.completeSubmitting = true
            this.error = ''

            ResearchReviewApi
                .completeProcess(this.process.processId, { decision })
                .then(() => {
                    this.$emit('completed', this.process)
                    this.$emit('close')
                })
                .catch(error => {
                    this.error = this.getErrorMessage(error)
                })
                .finally(() => {
                    this.completeSubmitting = false
                })
        },
        saveActiveItem() {
            if (!this.$refs.processFlow || this.savingActiveItem) {
                return
            }

            this.savingActiveItem = true
            this.$refs.processFlow
                .saveActiveItem()
                .finally(() => {
                    this.savingActiveItem = false
                })
        },
        onHide() {
            this.$emit('close')
        },
        getErrorMessage(error) {
            if (error && error.response && error.response.data) {
                return error.response.data.message
                    || error.response.data.title
                    || error.response.data.detail
                    || this.$t('Component(Error):default')
            }

            return error && error.message ? error.message : this.$t('Component(Error):default')
        },
    },
}
</script>
